import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getRoomTypeCapacity } from './tourUtils'

interface FlashPrice {
  sellPrice: number
  luxPlusPrice?: number | null
  occupancy: number
}

export function getFirstFlashAccommodation(bundle: App.Tours.Bundle): App.Tours.FlashAccommodationBundleComponent | undefined {
  return bundle.components.find(
    (entry): entry is App.Tours.FlashAccommodationBundleComponent => entry.productType === 'flashAccommodation',
  )
}

function isTwinFlashOccupancy(occupancy: number | undefined): boolean {
  return occupancy === 2
}

export function getTwinFlashAccommodation(bundle: App.Tours.Bundle): App.Tours.FlashAccommodationBundleComponent | undefined {
  const accommodation = getFirstFlashAccommodation(bundle)
  return accommodation && isTwinFlashOccupancy(accommodation.occupancy) ? accommodation : undefined
}

export function getFlashAccommodationDate(bundle: App.Tours.Bundle, referenceDate: string): App.Tours.BundleDate | undefined {
  const component = getFirstFlashAccommodation(bundle)
  const departureDate = moment.parseZone(referenceDate, moment.ISO_8601, true)
  if (!referenceDate || !departureDate.isValid()) return undefined
  const departureDay = departureDate.format(ISO_DATE_FORMAT)
  return component?.dates.find(date => {
    if (!date.referenceDate) return false
    const bundleDate = moment.parseZone(date.referenceDate, moment.ISO_8601, true)
    return bundleDate.isValid() && bundleDate.format(ISO_DATE_FORMAT) === departureDay
  })
}

export function getFlashAccommodationPrice(bundle: App.Tours.Bundle, referenceDate: string): FlashPrice | undefined {
  const flashComponent = getTwinFlashAccommodation(bundle)
  const date = getFlashAccommodationDate(bundle, referenceDate)
  const sellPrice = date?.sellPrice
  const occupancy = flashComponent?.occupancy
  if (sellPrice === null || sellPrice === undefined || occupancy === undefined) return undefined

  return { sellPrice, luxPlusPrice: date?.luxPlusPrice, occupancy }
}

export function getFlashPricePerPersonForOption(
  option: App.Tours.TourV2OfferPurchasableOption,
  flashSellPrice: number,
  flashOccupancy: number,
): number | undefined {
  if (!isTwinFlashOccupancy(flashOccupancy)) return undefined
  const occupancy = getRoomTypeCapacity(option.roomType)
  if (occupancy === 1) return flashSellPrice
  if (occupancy === 2) return Math.ceil(flashSellPrice / 2)
}

function clearBundledOption(option: App.Tours.TourV2OfferPurchasableOption): App.Tours.TourV2OfferPurchasableOption {
  if (!option.ltleBundleId) return option
  const { ltleBundleId: _bundleId, bundledPrice: _price, bundleMemberPrice: _memberPrice, ...unbundled } = option
  return unbundled
}

function addBundledPriceToOption(
  option: App.Tours.TourV2OfferPurchasableOption,
  flashPrice: FlashPrice,
  hasMemberPrices: boolean,
  bundleId: string,
): App.Tours.TourV2OfferPurchasableOption {
  const flashPricePerPerson = getFlashPricePerPersonForOption(option, flashPrice.sellPrice, flashPrice.occupancy)
  if (flashPricePerPerson === undefined) return clearBundledOption(option)
  const hotelMemberPrice = flashPrice.luxPlusPrice && flashPrice.luxPlusPrice > 0 ? flashPrice.luxPlusPrice : flashPrice.sellPrice
  const flashMemberPricePerPerson = hasMemberPrices && option.memberPrice > 0
    ? getFlashPricePerPersonForOption(option, hotelMemberPrice, flashPrice.occupancy)
    : undefined
  const { bundleMemberPrice: _previousBundleMemberPrice, ...optionWithoutMemberBundle } = option
  return {
    ...optionWithoutMemberBundle,
    ltleBundleId: bundleId,
    bundledPrice: option.price + flashPricePerPerson,
    ...(flashMemberPricePerPerson !== undefined ? { bundleMemberPrice: option.memberPrice + flashMemberPricePerPerson } : {}),
  }
}

function isAUDDeparture(departure: App.Tours.TourV2OfferDeparture | undefined): boolean {
  // Public tour responses can omit this field; the caller also checks the offer's current currency.
  return !!departure && (!departure.currencyCode || departure.currencyCode === 'AUD')
}

function addBundledPriceToDeparture(
  departure: App.Tours.TourV2OfferDeparture,
  bundle: App.Tours.Bundle,
  hasMemberPrices: boolean,
): App.Tours.TourV2OfferDeparture {
  if (!isAUDDeparture(departure)) return departure
  const flashPrice = getFlashAccommodationPrice(bundle, departure.startDate)
  const flashDate = getFlashAccommodationDate(bundle, departure.startDate)
  const availableRooms = flashPrice && flashDate?.available === true && Number.isInteger(flashDate.availableRooms) && (flashDate.availableRooms ?? 0) > 0
    ? flashDate.availableRooms ?? 0
    : 0
  if (!flashPrice) {
    const { ltleBundleId: _bundleId, ...unbundled } = departure
    return { ...unbundled, ltleBundleId: bundle.id, ltleBundleAvailableRooms: 0, options: departure.options.map(clearBundledOption) }
  }

  const options = departure.options.map(option => addBundledPriceToOption(option, flashPrice, hasMemberPrices, bundle.id))
  const { ltleBundleId: _bundleId, ...unbundled } = departure
  return {
    ...unbundled,
    ltleBundleId: bundle.id,
    ltleBundleAvailableRooms: availableRooms,
    options,
  }
}

export default function addBundlePriceToTourOffer(
  offer: App.Tours.TourV2Offer,
  bundle: App.Tours.Bundle,
  currentCurrency: string,
): App.Tours.TourV2Offer {
  if (offer.source === 'ttc' || currentCurrency !== 'AUD') return offer
  const departures = Object.fromEntries(Object.entries(offer.departures).map(([id, departure]) => [
    id,
    addBundledPriceToDeparture(departure, bundle, !!offer.luxPlus.hasMemberPrices),
  ]))
  const purchasableOptions = offer.purchasableOptions.map(option => {
    const departure = departures[option.fkDepartureId]
    const flashPrice = departure && isAUDDeparture(departure) ? getFlashAccommodationPrice(bundle, departure.startDate) : undefined
    return flashPrice ? addBundledPriceToOption(option, flashPrice, !!offer.luxPlus.hasMemberPrices, bundle.id) : clearBundledOption(option)
  })
  const variations = Object.fromEntries(Object.entries(offer.variations).map(([id, variation]) => [
    id,
    {
      ...variation,
      departures: variation.departures.map(departure =>
        departures[departure.id] ?? addBundledPriceToDeparture(departure, bundle, !!offer.luxPlus.hasMemberPrices),
      ),
    },
  ]))
  const hasBundle = Object.values(departures).some(departure => departure.ltleBundleId === bundle.id) ||
    purchasableOptions.some(option => option.ltleBundleId === bundle.id) ||
    Object.values(variations).some(variation => variation.departures.some(departure => departure.ltleBundleId === bundle.id))
  if (!hasBundle && !offer.ltleBundleId) return offer
  const { ltleBundleId: _bundleId, ...unbundledOffer } = offer

  return {
    ...unbundledOffer,
    ...(hasBundle ? { ltleBundleId: bundle.id } : {}),
    departures,
    purchasableOptions,
    variations,
  }
}
